import type { ReactElement } from 'react';
import { formatDate } from '@xdocs/i18n';
import type { AuditEvent } from '@xdocs/schemas';
import { DataTable, useI18n } from '@xdocs/ui';
import type { ColumnDef } from '@xdocs/ui';
import { useAdminT, useAdminUserActivity } from '../../../../features/admin/index.js';
import { optionalProp } from '../../../../lib/optionalProp.js';

export interface ActivityTabProps {
  userId: string;
}

/** This task's own addition beyond the mockup's 4 tabs — F49's user-scoped
 * `audit_events` read, applied to a THIRD party (the admin is looking at
 * someone else's activity rather than their own). Server-side excludes
 * rows attributed to impersonation sessions (§24). */
export function ActivityTab({ userId }: ActivityTabProps): ReactElement {
  const { locale } = useI18n();
  const { t } = useAdminT();
  const { rows, total, nextCursor, query, onQueryChange, onLoadMore, isLoading, isLoadingMore, isError, loadMoreError, onRetry } = useAdminUserActivity(userId);

  const columns: ColumnDef<AuditEvent>[] = [
    { id: 'action', header: t('admin:userDetail.activity.column.action'), primary: true, cell: (row) => <span>{row.action}</span> },
    {
      id: 'targetType',
      header: t('admin:userDetail.activity.column.target'),
      cell: (row) => <span>{row.targetType ? `${row.targetType}${row.targetId ? ` · ${row.targetId}` : ''}` : '—'}</span>,
    },
    {
      id: 'createdAt',
      header: t('admin:userDetail.activity.column.when'),
      sortable: true,
      cell: (row) => <span>{formatDate(locale, row.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}</span>,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      query={query}
      onQueryChange={onQueryChange}
      label={t('admin:userDetail.tabs.activity')}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      total={total}
      nextCursor={nextCursor}
      onLoadMore={onLoadMore}
      isLoadingMore={isLoadingMore}
      {...optionalProp('loadMoreError', loadMoreError)}
      emptyTitle={t('admin:userDetail.activity.empty.title')}
      emptyDescription={t('admin:userDetail.activity.empty.description')}
      noResultsTitle={t('admin:userDetail.activity.noResults.title')}
    />
  );
}
